import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Mail, MessageSquare, FileText, HelpCircle, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { COMPANY_INFO } from '../data/company';
import { SUPPORT_FAQS } from '../data/supportFaqs';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const SupportPage = () => {
  const phoneNumbers = COMPANY_INFO.contact.phone || [];

  const supportOptions = [
    {
      icon: Phone,
      title: 'Phone Support',
      description: 'Speak directly to our technical experts for product and service guidance',
      action: phoneNumbers,
      actionType: 'tel',
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send detailed product, calibration, or installation queries',
      action: COMPANY_INFO.contact.email[0],
      actionType: 'email',
    },
    {
      icon: MessageSquare,
      title: 'Service Request',
      description: 'Log a service or quotation request online',
      action: '/contact',
      actionType: 'link',
    },
  ];

  return (
    <div data-testid="support-page">
      <Seo {...routeMeta['/support']} path="/support" />
      {/* Hero Section */}
      <section className="bg-[#0B1F3B] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Support</span>
            </div>
            <h1 className="font-['Manrope'] text-4xl md:text-6xl font-extrabold text-white uppercase tracking-tight">
              Support Center
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Get help with weighbridges, weighing scales, indicators, software, calibration, and service requirements across India, Ghana, Africa, and international markets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-12 bg-[#08152b] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 -mt-16 relative z-10">
            {supportOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#102548]/95 border border-white/15 p-8 shadow-lg"
              >
                <div className="w-14 h-14 bg-[#1E40AF] flex items-center justify-center mb-6">
                  <option.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{option.title}</h3>
                <p className="text-slate-200 mb-6">{option.description}</p>
                {option.actionType === 'tel' && (
                  <div className="space-y-2">
                    {option.action.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="block">
                        <Button className="tech-shimmer w-full rounded-sm bg-[#1E40AF] font-semibold uppercase tracking-wider text-white hover:bg-[#2563EB]">
                          {phone}
                        </Button>
                      </a>
                    ))}
                  </div>
                )}
                {option.actionType === 'email' && (
                  <a href={`mailto:${option.action}`}>
                    <Button className="tech-shimmer w-full rounded-sm bg-[#1E40AF] font-semibold uppercase tracking-wider text-white hover:bg-[#2563EB]">
                      Send Email
                    </Button>
                  </a>
                )}
                {option.actionType === 'link' && (
                  <Link to={option.action}>
                    <Button className="tech-shimmer w-full rounded-sm bg-[#1E40AF] font-semibold uppercase tracking-wider text-white hover:bg-[#2563EB]">
                      Submit Request
                    </Button>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-[#08152b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono text-[#1E40AF] uppercase tracking-widest">Our Services</span>
            <h2 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-white mt-4 uppercase tracking-tight">
              How We Can Help
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Wrench, title: 'Installation', description: 'Professional installation by certified engineers' },
              { icon: HelpCircle, title: 'Calibration', description: 'Certified calibration services meeting legal standards' },
              { icon: FileText, title: 'AMC', description: 'Annual Maintenance Contracts for worry-free operation' },
              { icon: MessageSquare, title: 'Training', description: 'Comprehensive operator and maintenance training' },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 p-6 border-t-4 border-t-[#60A5FA]"
              >
                <service.icon className="h-10 w-10 text-[#1E40AF] mb-4" />
                <h3 className="font-bold text-white mb-2">{service.title}</h3>
                <p className="text-slate-200 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24 bg-[#08152b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono text-[#1E40AF] uppercase tracking-widest">FAQs</span>
            <h2 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-white mt-4 uppercase tracking-tight">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {SUPPORT_FAQS.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="bg-white/5 border border-white/15 px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-white hover:text-[#93C5FD] py-6 [&>svg]:text-[#93C5FD]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent forceMount className="text-slate-200 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Can't Find What You Need?
            </h3>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Our support team is ready to help with weighing system questions for India, Ghana, Africa, Dubai, and global industrial sites.
            </p>
            <Link to="/contact">
              <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-5 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]" data-testid="support-contact-btn">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#08152b] py-12">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          <div id="privacy-policy" className="border border-white/10 bg-white/5 p-6">
            <h2 className="text-lg font-bold uppercase tracking-wider text-white">Privacy Policy</h2>
            <p className="mt-3 text-sm leading-6 text-slate-200">
              Enquiry details are used only to respond to product, service, and support requests. Website analytics are first-party and stored without selling visitor data to advertising platforms.
            </p>
          </div>
          <div id="terms-of-service" className="border border-white/10 bg-white/5 p-6">
            <h2 className="text-lg font-bold uppercase tracking-wider text-white">Terms of Service</h2>
            <p className="mt-3 text-sm leading-6 text-slate-200">
              Product specifications, installation timelines, warranty terms, and service commitments are confirmed during quotation or contract review for each project.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
